// src/components/notifications/NotificationBell.tsx
//
// The header notification bell (Story 4.9). A quiet bell with a brand-600 unread
// count badge (hidden entirely at zero — never a "0" badge), the toggle for the
// popover panel, and the aria-live="polite" region that announces new arrivals
// (EXPERIENCE.md's accessibility floor). It is CHROME on every page: it degrades
// quietly (a failed load renders the bare bell, no badge) and never raises a
// page-level banner. The count updates live via useNotifications' shared realtime
// refetch.

import { useState, useRef, useCallback } from 'react'
import { Bell } from 'lucide-react'
import { useNotifications } from '../../hooks/useNotifications'
import {
  unreadCountDisplay,
  unreadCountLabel,
} from '../../utils/notificationDisplay'
import { NotificationPanel } from './NotificationPanel'

export function NotificationBell() {
  const { notifications, unreadCount, markingAll, markRead, markAllRead } =
    useNotifications()
  const [open, setOpen] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [prevCount, setPrevCount] = useState(unreadCount)
  const bellRef = useRef<HTMLButtonElement>(null)

  // Announce only INCREASES (new arrivals) — not a decrement from marking read.
  // React's "adjust state during render" pattern (guarded by a change): no effect,
  // no cascading render, no focus-stealing (the region is polite + sr-only).
  if (unreadCount !== prevCount) {
    if (unreadCount > prevCount) {
      setAnnouncement(unreadCountLabel(unreadCount))
    }
    setPrevCount(unreadCount)
  }

  const close = useCallback(() => {
    setOpen(false)
    // Focus returns to the bell on close (1.1's stable fallback: the button is
    // always focusable; tabIndex keeps it a programmatic focus target).
    bellRef.current?.focus()
  }, [])

  const hasUnread = unreadCount > 0

  return (
    <div className="relative">
      <button
        ref={bellRef}
        type="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={hasUnread ? unreadCountLabel(unreadCount) : 'Notifications'}
        className="relative p-1 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {hasUnread && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[0.65rem] font-medium leading-none text-white bg-brand-600 rounded-full"
          >
            {unreadCountDisplay(unreadCount)}
          </span>
        )}
      </button>

      {/* Polite live region: new arrivals announce without stealing focus. */}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {open && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          markingAll={markingAll}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onClose={close}
          anchorRef={bellRef}
        />
      )}
    </div>
  )
}
